/**
 * EarlyCareerHome — /early-career
 *
 * Public cold-traffic landing page for "Proof-Forge", Career Forge's
 * founding-cohort program. Reachable by logged-out visitors (no
 * ProtectedRoute), exactly like /portfolio-kit.
 *
 * Ported from the standalone design reference (proof_forge_landing.html) to
 * our conventions: colours come entirely from the MUI theme palette so the
 * page respects light/dark mode like every other page, typography uses the
 * native 400/500 scale (eyebrow overline is the one 700 exception), and the
 * staggered fadeUp animation matches PortfolioKitHome / CareerTransitionHome.
 *
 * Section order (mirrors the reference):
 *   1. Hero — the honest thesis: "You have the skills. You're missing the proof."
 *   2. The signal gap (problem framing — signal, not skills)
 *   3. The honest comparison (truth table: us vs. $15k coaches)
 *   4. We show you the rubric (the PF-1 5-dimension rubric, shown not claimed)
 *   5. The four-step path (Position / Build / Signal / Convert)
 *   6. The $50 founding-cohort offer box
 *   7. The radical-transparency note
 *   8. A quiet email-capture fallback
 *
 * Launch gate — fill these before deploy (see landing_deployment_handoff.md):
 *   STRIPE_PAYMENT_LINK_URL, COHORT_START_DATE, EMAIL_CAPTURE_ENDPOINT.
 * No localStorage/sessionStorage is used anywhere (per spec).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  track,
  useScrollDepth,
  usePageTimer,
  useExitTracking,
  useIntersectionTracker,
} from "../utils/analytics";
import { getUtmParams, utmClientReferenceId } from "../utils/utm";

// ---------------------------------------------------------------------------
// Launch-gate constants — fill before deploy. Intentionally "TODO" so the page
// fails loud (disabled CTA / no-op submit) rather than shipping a broken link.
// ---------------------------------------------------------------------------

// `: string` annotations are required: without them TS narrows each const to its
// literal type, making the `!== "TODO"` sentinel guards below a TS2367 "no overlap"
// compile error once the placeholder is filled in.
// NOTE: STRIPE_PAYMENT_LINK_URL is the Stripe LIVE-mode link — clicking it takes real $50 deposits.
const STRIPE_PAYMENT_LINK_URL: string = "https://buy.stripe.com/6oU3cv5VZ7855Hv50v1oI00"; // $50 founding-deposit Stripe Payment Link (Live)
const COHORT_START_DATE: string = "Monday, August 24, 2026"; // Cohort 1 start — NOT CURRENTLY OFFERED, see below

// Deposits closed 2026-08-04. The pre-committed deposit test
// (docs/04-gtm/proof-forge/proof_forge_test_protocol.md) returned VOID FOR TRAFFIC:
// 1 paid deposit ever, dated 2026-07-01, untagged — the owner's own smoke test.
// The ≥300-cold-visitor validity gate was never approached, so the curriculum build
// (gated on ≥8 deposits) never started. Taking a $50 deposit for a dated seat we
// cannot deliver would break the protocol's own "two-way promise" term, so the page
// runs as a waitlist until Cohort 1 is genuinely scheduled AND built.
//
// To reopen: set REACT_APP_COHORT_1_SCHEDULED="true" on the Railway frontend service
// (build-time var — requires a rebuild), set COHORT_START_DATE to the real date, and
// confirm the Stripe Payment Link is active. Deposits are gated on BOTH.
//
// Reading it from env rather than hardcoding `false` is deliberate: it keeps BOTH
// branches reachable from tests, so the deposit-CTA coverage that existed before
// this change survives instead of being deleted. Anything other than the exact
// string "true" resolves to closed — the safe failure mode for a money surface.
const COHORT_1_SCHEDULED: boolean = process.env.REACT_APP_COHORT_1_SCHEDULED === "true";
const EMAIL_CAPTURE_ENDPOINT: string = "https://formspree.io/f/mqevyykp"; // Formspree POST endpoint for the email fallback

// ---------------------------------------------------------------------------
// Animation helpers — match PortfolioKitHome
// ---------------------------------------------------------------------------

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: "fadeUp 0.5s ease both",
};

function withDelay(ms: number) {
  return { ...fadeUp, animationDelay: `${ms}ms` };
}

const OFFER_ANCHOR_ID = "offer";
const EMAIL_CAPTURE_ANCHOR_ID = "waitlist";

// ---------------------------------------------------------------------------
// Content data
// ---------------------------------------------------------------------------

const HONEST_CHIP_KEYS = [
  "EarlyCareerHome.honestChipNoGuaranteedJob",
  "EarlyCareerHome.honestChipFiftyRefundable",
  "EarlyCareerHome.honestChipFoundingCohort",
] as const;

// While deposits are closed the "$50, fully refundable" chip would advertise a
// transaction the page will not perform. Swap it for the honest status.
const WAITLIST_CHIP_KEYS = [
  "EarlyCareerHome.honestChipNoGuaranteedJob",
  "EarlyCareerHome.waitlistChipNoPaymentToday",
  "EarlyCareerHome.honestChipFoundingCohort",
] as const;

// The honest comparison — "them" (most programs / $15k coaches) vs. "us".
const TRUTH_ROW_KEYS = [
  {
    them: "EarlyCareerHome.truthRow1Them",
    us: "EarlyCareerHome.truthRow1Us",
  },
  {
    them: "EarlyCareerHome.truthRow2Them",
    us: "EarlyCareerHome.truthRow2Us",
  },
  {
    them: "EarlyCareerHome.truthRow3Them",
    us: "EarlyCareerHome.truthRow3Us",
  },
  {
    them: "EarlyCareerHome.truthRow4Them",
    us: "EarlyCareerHome.truthRow4Us",
  },
  {
    them: "EarlyCareerHome.truthRow5Them",
    us: "EarlyCareerHome.truthRow5Us",
  },
] as const;

// The PF-1 rubric for the core "Recruiter-Ready Proof Project" — shown, not claimed.
const RUBRIC_DIMENSION_KEYS = [
  {
    k: "EarlyCareerHome.rubricRoleFitTitle",
    d: "EarlyCareerHome.rubricRoleFitBody",
  },
  {
    k: "EarlyCareerHome.rubricShippedLiveTitle",
    d: "EarlyCareerHome.rubricShippedLiveBody",
  },
  {
    k: "EarlyCareerHome.rubricRepoQualityTitle",
    d: "EarlyCareerHome.rubricRepoQualityBody",
  },
  {
    k: "EarlyCareerHome.rubricDecisionRecordTitle",
    d: "EarlyCareerHome.rubricDecisionRecordBody",
  },
  {
    k: "EarlyCareerHome.rubricRecruiterLegibilityTitle",
    d: "EarlyCareerHome.rubricRecruiterLegibilityBody",
  },
] as const;

// The four-step path — a real sequence, so numbering is earned.
const PATH_STEP_KEYS = [
  {
    title: "EarlyCareerHome.pathStep1Title",
    body: "EarlyCareerHome.pathStep1Body",
    outcome: "EarlyCareerHome.pathStep1Outcome",
  },
  {
    title: "EarlyCareerHome.pathStep2Title",
    body: "EarlyCareerHome.pathStep2Body",
    outcome: "EarlyCareerHome.pathStep2Outcome",
  },
  {
    title: "EarlyCareerHome.pathStep3Title",
    body: "EarlyCareerHome.pathStep3Body",
    outcome: "EarlyCareerHome.pathStep3Outcome",
  },
  {
    title: "EarlyCareerHome.pathStep4Title",
    body: "EarlyCareerHome.pathStep4Body",
    outcome: "EarlyCareerHome.pathStep4Outcome",
  },
] as const;

// Offer bullet i18n keys — rendered via <Trans> inside OfferSection (needs `t`
// for the embedded-markup + COHORT_START_DATE interpolation, so the bullets
// themselves are built inside the component rather than at module scope).
const OFFER_BULLET_KEYS = [
  "EarlyCareerHome.offerBullet1",
  "EarlyCareerHome.offerBullet2",
  "EarlyCareerHome.offerBullet3",
  "EarlyCareerHome.offerBullet4",
] as const;

// Shown instead of OFFER_BULLET_KEYS while deposits are closed. Deliberately does
// NOT interpolate {{cohortStartDate}} — there is no date to state yet, and stating
// one is exactly the promise we cannot keep.
const WAITLIST_BULLET_KEYS = [
  "EarlyCareerHome.waitlistBullet1",
  "EarlyCareerHome.waitlistBullet2",
  "EarlyCareerHome.waitlistBullet3",
] as const;

// ---------------------------------------------------------------------------
// Small shared building blocks
// ---------------------------------------------------------------------------

function Eyebrow({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Typography
      variant="overline"
      fontWeight={700}
      sx={{
        color: theme.palette.primary.main,
        letterSpacing: "0.14em",
        display: "block",
      }}
    >
      {children}
    </Typography>
  );
}

/** Section wrapper with optional subtle tint band (alternating rhythm). */
function Section({
  tint = false,
  delay = 0,
  children,
}: {
  tint?: boolean;
  delay?: number;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box sx={{ bgcolor: tint ? alpha(theme.palette.primary.main, 0.03) : "transparent" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(delay) }}>
        {children}
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 1. Hero
// ---------------------------------------------------------------------------

function Hero() {
  const theme = useTheme();
  const { search } = useLocation();
  const { t } = useTranslation("pages-core");

  const scrollToOffer = () => {
    track("early_career.hero_cta_click", {
      location: "hero",
      ...getUtmParams(search),
    });
    document
      .getElementById(OFFER_ANCHOR_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        py: { xs: 6, md: 9 },
        ...withDelay(0),
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={2.5} alignItems="flex-start" sx={{ maxWidth: 860 }}>
          <Eyebrow>{t("EarlyCareerHome.heroEyebrow")}</Eyebrow>
          <Typography
            variant="h2"
            fontWeight={500}
            sx={{
              letterSpacing: "-0.02em",
              fontSize: { xs: "2.2rem", md: "3.3rem" },
              lineHeight: 1.08,
            }}
          >
            {t("EarlyCareerHome.heroTitleLine1")}
            <br />
            <Trans
              i18nKey="EarlyCareerHome.heroTitleLine2"
              t={t}
              components={[
                <Box key="span0" component="span" sx={{ color: theme.palette.primary.main }} />,
              ]}
            />
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, maxWidth: 620, lineHeight: 1.55 }}
          >
            {t("EarlyCareerHome.heroSubtitle")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {(COHORT_1_SCHEDULED ? HONEST_CHIP_KEYS : WAITLIST_CHIP_KEYS).map((key) => (
              <Chip
                key={key}
                label={t(key)}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 500,
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  color: "text.secondary",
                }}
              />
            ))}
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={scrollToOffer}
              sx={{ fontWeight: 500, px: 3.5 }}
            >
              {t(
                COHORT_1_SCHEDULED
                  ? "EarlyCareerHome.heroCtaButton"
                  : "EarlyCareerHome.waitlistHeroCtaButton",
              )}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t(
                COHORT_1_SCHEDULED
                  ? "EarlyCareerHome.heroCtaCaption"
                  : "EarlyCareerHome.waitlistHeroCtaCaption",
              )}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 2. The signal gap
// ---------------------------------------------------------------------------

function SignalGapSection() {
  const { t } = useTranslation("pages-core");
  return (
    <Section delay={80}>
      <Eyebrow>{t("EarlyCareerHome.signalGapEyebrow")}</Eyebrow>
      <Typography variant="h4" fontWeight={500} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.015em", mt: 1, mb: 2.5 }}>
        {t("EarlyCareerHome.signalGapTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mb: 2.5, lineHeight: 1.7, fontSize: "1.05rem" }}>
        <Trans
          i18nKey="EarlyCareerHome.signalGapBody1"
          t={t}
          components={[
            <Box key="strong0" component="strong" sx={{ color: "text.primary" }} />,
          ]}
        />
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.7, fontSize: "1.05rem" }}>
        <Trans
          i18nKey="EarlyCareerHome.signalGapBody2"
          t={t}
          components={[
            <Box key="strong0" component="strong" sx={{ color: "text.primary" }} />,
          ]}
        />
      </Typography>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 3. The honest comparison (truth table)
// ---------------------------------------------------------------------------

function TruthTableSection() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");

  return (
    <Section tint delay={120}>
      <Eyebrow>{t("EarlyCareerHome.truthTableEyebrow")}</Eyebrow>
      <Typography variant="h4" fontWeight={500} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.015em", mt: 1, mb: 1.5 }}>
        {t("EarlyCareerHome.truthTableTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mb: 4, lineHeight: 1.6 }}>
        {t("EarlyCareerHome.truthTableIntro")}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          overflow: "hidden",
        }}
      >
        {/* Header row */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          <Box
            sx={{
              px: 3,
              py: 1.75,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRight: { sm: `1px solid ${theme.palette.divider}` },
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: "0.08em" }}>
              {t("EarlyCareerHome.truthTableThemHeader")}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 1.75,
              bgcolor: theme.palette.identity.main,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="overline" fontWeight={700} sx={{ color: theme.palette.identity.contrastText, letterSpacing: "0.08em" }}>
              {t("EarlyCareerHome.truthTableUsHeader")}
            </Typography>
          </Box>
        </Box>

        {/* Comparison rows */}
        {TRUTH_ROW_KEYS.map((row, idx) => (
          <Box
            key={row.us}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              borderTop: idx === 0 ? "none" : undefined,
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2.25,
                bgcolor: alpha(theme.palette.text.primary, 0.015),
                borderRight: { sm: `1px solid ${theme.palette.divider}` },
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                <Box component="span" sx={{ color: "text.disabled", mr: 1 }}>→</Box>
                {t(row.them)}
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                <Box component="span" sx={{ color: theme.palette.success.dark, fontWeight: 700, mr: 1 }}>✓</Box>
                {t(row.us)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Paper>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 4. We show you the rubric
// ---------------------------------------------------------------------------

function RubricSection() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");

  return (
    <Section delay={160}>
      <Eyebrow>{t("EarlyCareerHome.rubricEyebrow")}</Eyebrow>
      <Typography variant="h4" fontWeight={500} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.015em", mt: 1, mb: 1.5 }}>
        {t("EarlyCareerHome.rubricTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mb: 4, lineHeight: 1.6 }}>
        {t("EarlyCareerHome.rubricIntro")}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          maxWidth: 760,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ color: theme.palette.primary.main }}>
            {t("EarlyCareerHome.rubricCardHeader")}
          </Typography>
        </Box>
        {RUBRIC_DIMENSION_KEYS.map((dim, idx) => (
          <Box
            key={dim.k}
            sx={{ px: 3, py: 2, borderTop: idx === 0 ? "none" : `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ mb: 0.5 }}>
              {t(dim.k)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              {t(dim.d)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ px: 3, py: 1.75, bgcolor: alpha(theme.palette.text.primary, 0.02), borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary">
            {t("EarlyCareerHome.rubricPassThresholdCaption")}
          </Typography>
        </Box>
      </Paper>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 5. The four-step path
// ---------------------------------------------------------------------------

function PathSection() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");

  return (
    <Section tint delay={200}>
      <Eyebrow>{t("EarlyCareerHome.pathEyebrow")}</Eyebrow>
      <Typography variant="h4" fontWeight={500} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.015em", mt: 1, mb: 1.5 }}>
        {t("EarlyCareerHome.pathTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mb: 4, lineHeight: 1.6 }}>
        {t("EarlyCareerHome.pathIntro")}
      </Typography>

      <Stack spacing={2}>
        {PATH_STEP_KEYS.map((step, idx) => (
          <Paper
            key={step.title}
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: { xs: 2, md: 2.5 },
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: theme.palette.identity.main,
                color: theme.palette.identity.contrastText,
                display: "grid",
                placeItems: "center",
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {idx + 1}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={500} sx={{ mb: 0.75 }}>
                {t(step.title)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1.5 }}>
                {t(step.body)}
              </Typography>
              <Chip
                label={t("EarlyCareerHome.pathOutcomeLabel", { outcome: t(step.outcome) })}
                size="small"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.success.dark,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                }}
              />
            </Box>
          </Paper>
        ))}
      </Stack>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 6 + 7. The honest offer box + radical-transparency note
// ---------------------------------------------------------------------------

function OfferSection() {
  const theme = useTheme();
  const { search } = useLocation();
  const { t } = useTranslation("pages-core");
  // Deposits require BOTH a configured link and a genuinely scheduled cohort.
  // `stripeReady` alone is not enough — see COHORT_1_SCHEDULED above.
  const stripeReady = STRIPE_PAYMENT_LINK_URL !== "TODO";
  const depositsOpen = stripeReady && COHORT_1_SCHEDULED;

  // Fires once when the offer box scrolls into view — the "reached the offer"
  // funnel step.
  const offerRef = useIntersectionTracker("early_career.offer_seen");

  const utm = useMemo(() => getUtmParams(search), [search]);

  // Forward the traffic source to Stripe via client_reference_id so each $50
  // deposit can be attributed to its UTM source in the Stripe dashboard.
  const stripeHref = useMemo(() => {
    if (!stripeReady) return STRIPE_PAYMENT_LINK_URL;
    const crid = utmClientReferenceId(utm);
    if (!crid) return STRIPE_PAYMENT_LINK_URL;
    const sep = STRIPE_PAYMENT_LINK_URL.includes("?") ? "&" : "?";
    return `${STRIPE_PAYMENT_LINK_URL}${sep}client_reference_id=${encodeURIComponent(crid)}`;
  }, [stripeReady, utm]);

  return (
    <Box id={OFFER_ANCHOR_ID}>
      <Section delay={240}>
        <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
          <Eyebrow>{t("EarlyCareerHome.offerEyebrow")}</Eyebrow>
          <Typography variant="h4" fontWeight={500} sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.015em" }}>
            {t(depositsOpen ? "EarlyCareerHome.offerTitle" : "EarlyCareerHome.waitlistOfferTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, lineHeight: 1.6 }}>
            {t(depositsOpen ? "EarlyCareerHome.offerSubtitle" : "EarlyCareerHome.waitlistOfferSubtitle")}
          </Typography>
        </Stack>

        <Paper
          elevation={0}
          ref={offerRef}
          sx={{
            maxWidth: 560,
            mx: "auto",
            borderRadius: 4,
            border: `2px solid ${theme.palette.identity.main}`,
            overflow: "hidden",
          }}
        >
          {/* Offer top — navy panel (identity slot: rare marketing panel) */}
          <Box sx={{ bgcolor: theme.palette.identity.main, color: theme.palette.identity.contrastText, px: 4, py: 3, textAlign: "center" }}>
            <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: "0.12em", opacity: 0.85 }}>
              {t(depositsOpen ? "EarlyCareerHome.offerDepositLabel" : "EarlyCareerHome.waitlistPanelLabel")}
            </Typography>
            {depositsOpen ? (
              <Typography sx={{ fontWeight: 500, fontSize: "2.6rem", lineHeight: 1.1, mt: 0.5 }}>
                <Trans
                  i18nKey="EarlyCareerHome.offerPriceLine"
                  t={t}
                  components={[
                    <Box key="span0" component="span" sx={{ fontSize: "1rem", fontWeight: 400, opacity: 0.8 }} />,
                  ]}
                />
              </Typography>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: "1.9rem", lineHeight: 1.15, mt: 0.5 }}>
                {t("EarlyCareerHome.waitlistPanelLine")}
              </Typography>
            )}
          </Box>

          {/* Offer body */}
          <Box sx={{ px: 4, py: 3.5 }}>
            <Stack spacing={1.75}>
              {(depositsOpen ? OFFER_BULLET_KEYS : WAITLIST_BULLET_KEYS).map((key) => (
                <Stack key={key} direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.dark, flexShrink: 0, mt: 0.2 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <Trans
                      i18nKey={key}
                      t={t}
                      values={{ cohortStartDate: COHORT_START_DATE }}
                      components={[<Box key="strong0" component="strong" />]}
                    />
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* Offer CTA */}
          <Box sx={{ px: 4, pb: 4, textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              endIcon={<ArrowForwardIcon />}
              {...(depositsOpen
                ? {
                    component: "a",
                    href: stripeHref,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    onClick: () =>
                      track("early_career.deposit_cta_click", {
                        location: "offer",
                        ...utm,
                      }),
                  }
                : {
                    // Waitlist mode: send them to the email capture, not to Stripe.
                    onClick: () => {
                      track("early_career.waitlist_cta_click", { location: "offer", ...utm });
                      document
                        .getElementById(EMAIL_CAPTURE_ANCHOR_ID)
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    },
                  })}
              sx={{ fontWeight: 500 }}
            >
              {t(depositsOpen ? "EarlyCareerHome.offerCtaButton" : "EarlyCareerHome.waitlistOfferCtaButton")}
            </Button>
            {!depositsOpen && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {t(
                  stripeReady
                    ? "EarlyCareerHome.waitlistOfferCtaCaption"
                    : "EarlyCareerHome.offerPaymentLinkPendingCaption",
                )}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* The radical-transparency note — the brand's spine, stated plainly */}
        <Box
          sx={{
            maxWidth: 760,
            mx: "auto",
            mt: 5,
            px: { xs: 3, md: 3.5 },
            py: 3,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: "0 12px 12px 0",
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <Trans
              i18nKey="EarlyCareerHome.offerTransparencyNote"
              t={t}
              components={[<Box key="strong0" component="strong" />]}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t("EarlyCareerHome.offerTransparencySignature")}
          </Typography>
        </Box>
      </Section>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// 8. Quiet email-capture fallback
// ---------------------------------------------------------------------------

function EmailFallbackSection() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");

  const endpointReady = EMAIL_CAPTURE_ENDPOINT !== "TODO";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;

    track("early_career.email_submit", { endpoint_configured: endpointReady });

    // Endpoint not wired yet: render fine, no-op gracefully (no network, no crash).
    if (!endpointReady) {
      setStatus("done");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch(EMAIL_CAPTURE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: value }),
      });
      setStatus(res.ok ? "done" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <Section tint delay={280}>
      <Box
        id={EMAIL_CAPTURE_ANCHOR_ID}
        sx={{
          maxWidth: 560,
          mx: "auto",
          px: { xs: 3, md: 4 },
          py: { xs: 3.5, md: 4 },
          textAlign: "center",
          borderRadius: 3,
          border: `1px dashed ${alpha(theme.palette.identity.main, 0.4)}`,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
          {t("EarlyCareerHome.emailFallbackTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          {t("EarlyCareerHome.emailFallbackBody")}
        </Typography>

        {status === "done" ? (
          <Stack spacing={1} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CheckCircleIcon sx={{ color: theme.palette.success.dark, fontSize: 20 }} />
              <Typography variant="body2">
                {t("EarlyCareerHome.emailFallbackDoneMessage")}
              </Typography>
            </Stack>
            <Typography variant="body2">
              <Link component={RouterLink} to="/early-career/teardown" color="primary" underline="hover" fontWeight={500}>
                {t("EarlyCareerHome.emailFallbackTeardownLinkText")}
              </Link>
            </Typography>
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ maxWidth: 440, mx: "auto" }}>
              <TextField
                type="email"
                required
                fullWidth
                size="small"
                placeholder={t("EarlyCareerHome.emailFallbackPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ "aria-label": t("EarlyCareerHome.emailFallbackAriaLabel") }}
              />
              <Button
                type="submit"
                variant="outlined"
                disabled={status === "submitting"}
                sx={{ fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {status === "submitting"
                  ? t("EarlyCareerHome.emailFallbackSendingLabel")
                  : t("EarlyCareerHome.emailFallbackSendButton")}
              </Button>
            </Stack>
            {status === "error" && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
                {t("EarlyCareerHome.emailFallbackErrorMessage")}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Footer — legal copy preserved exactly; policy links to existing routes
// ---------------------------------------------------------------------------

function PageFooter() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  return (
    <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, py: { xs: 5, md: 6 } }}>
      <Container maxWidth="lg">
        <Typography variant="body2" color="text.secondary">
          {t("EarlyCareerHome.footerLegalLine1")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("EarlyCareerHome.footerRefundNote")}{" "}
          <Link component={RouterLink} to="/refunds" color="primary" underline="hover">
            {t("EarlyCareerHome.footerRefundPolicyLink")}
          </Link>{" "}
          ·{" "}
          <Link component={RouterLink} to="/privacy" color="primary" underline="hover">
            {t("EarlyCareerHome.footerPrivacyLink")}
          </Link>
        </Typography>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function EarlyCareerHome() {
  const { search } = useLocation();

  // Page-level engagement signals (reuse the shared analytics hooks).
  useScrollDepth("early_career.scroll_depth");
  usePageTimer("early_career.time_on_page");
  useExitTracking("early_career.exit");

  // Page view + traffic source + UTM attribution, fired once on mount.
  useEffect(() => {
    track("early_career.page_view", {
      referrer: document.referrer || null,
      traffic_source: document.referrer
        ? new URL(document.referrer, window.location.href).hostname
        : "direct",
      ...getUtmParams(search),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Hero />
      <SignalGapSection />
      <TruthTableSection />
      <RubricSection />
      <PathSection />
      <OfferSection />
      <EmailFallbackSection />
      <PageFooter />
    </Box>
  );
}
